/**
 * Which IdP this build talks to, resolved at RUNTIME.
 *
 * Today `REACT_APP_AUTH0_DOMAIN` and `REACT_APP_AUTH0_CLIENTID` are baked into the JS
 * bundle at `docker build` time (Dockerfile build-args, four separate
 * publish-image*.yml workflows). That makes a per-environment IdP impossible: the
 * image IS the environment. Only `REACT_APP_AUTH0_CALLBACK_URL` is runtime, via
 * runtime-env-cra's window.__RUNTIME_CONFIG__.
 *
 * Everything here prefers the runtime value and falls back to the build-time one, so:
 *   - existing images keep working unchanged (fallback path), and
 *   - a single image can be pointed at Auth0 or Keycloak by environment alone
 *     (runtime path), once the vars are added to the Dockerfile whitelist.
 */

const runtime = (key) => {
  try {
    const found = window.__RUNTIME_CONFIG__?.[key]
    // "-" is the image's placeholder for an OPTIONAL key this environment leaves unset:
    // runtime-env-cra refuses empty values (and nginx then never starts), so the
    // Dockerfile fills optional auth keys with "-". Treat it exactly like absent.
    return found && found !== "-" ? found : undefined
  } catch {
    return undefined
  }
}

const value = (key) => runtime(key) || process.env[key] || undefined

const PROVIDERS = ["auth0", "keycloak"]

/**
 * The provider the SERVER says is active — Replane `auth.provider`, which operators can
 * flip without a redeploy. Fetched once by resolveAuthProvider() before the first render,
 * because the sign-in SDK is chosen once, at mount. Null until then, or if that lookup
 * failed, in which case the build's REACT_APP_AUTH_PROVIDER applies.
 */
let serverProvider = null

// A provider can only be started if this build has its public config. Accepting one it
// lacks (Keycloak selected on an environment whose client was never given the Keycloak
// issuer) would render nothing — so, like the server, that selection is refused.
const hasConfigFor = (provider) => {
  if (provider === "keycloak") return Boolean(value("REACT_APP_AUTH_ISSUER") && value("REACT_APP_AUTH_CLIENTID"))
  if (provider === "auth0") return Boolean(value("REACT_APP_AUTH0_DOMAIN") && value("REACT_APP_AUTH0_CLIENTID"))
  return false
}

// Trimmed, so "keycloak " no longer silently means Auth0.
const buildProvider = () => {
  const provider = String(value("REACT_APP_AUTH_PROVIDER") || "auth0").trim().toLowerCase()
  return PROVIDERS.includes(provider) ? provider : "auth0"
}

export const authProvider = () => serverProvider || buildProvider()

export const isKeycloak = () => authProvider() === "keycloak"

/**
 * Ask the server which provider is active. Bounded by a timeout, and never throws: any
 * failure leaves the build's default in place rather than blocking sign-in.
 */
export const resolveAuthProvider = async ({ timeoutMs = 3000 } = {}) => {
  const api = value("REACT_APP_API")
  if (!api) return authProvider()
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const res = await fetch(`${api}/public/auth/config`, { signal: controller.signal })
    if (res.ok) {
      const body = await res.json()
      const provider = String((body?.getAuthConfig ?? body)?.provider ?? "").trim().toLowerCase()
      if (PROVIDERS.includes(provider) && hasConfigFor(provider)) serverProvider = provider
    }
  } catch {
    // Unreachable or timed out — the build's default stands.
  } finally {
    clearTimeout(timer)
  }
  return authProvider()
}

/**
 * OIDC issuer for the ACTIVE provider. For Keycloak this is `{base}/realms/{realm}` and
 * MUST match the `iss` claim exactly — including the absence of a trailing slash, which
 * Auth0 has and Keycloak does not.
 *
 * Provider-specific on purpose: with both providers configured (which switching needs),
 * a fallback chain would hand the Auth0 SDK the Keycloak issuer, and a half-configured
 * Keycloak environment would silently run discovery against Auth0.
 */
export const authIssuer = () => {
  if (isKeycloak()) return (value("REACT_APP_AUTH_ISSUER") || "").replace(/\/$/, "")
  const domain = value("REACT_APP_AUTH0_DOMAIN")
  return domain ? `https://${domain}` : ""
}

// Same reasoning: `REACT_APP_AUTH_CLIENTID || REACT_APP_AUTH0_CLIENTID` gave Auth0 the
// Keycloak client id as soon as both were set.
export const authClientId = () => (isKeycloak() ? value("REACT_APP_AUTH_CLIENTID") : value("REACT_APP_AUTH0_CLIENTID")) || ""

export const authAudience = () => value("REACT_APP_AUTH_AUDIENCE") || "hasura"

/**
 * Origins of EVERY IdP this build can sign in with — both, when both are configured,
 * because the active one can be switched at runtime. Used to keep tracing headers off
 * IdP requests (they break OIDC discovery via CORS).
 */
export const idpOrigins = () => {
  const auth0Domain = value("REACT_APP_AUTH0_DOMAIN")
  return [value("REACT_APP_AUTH_ISSUER"), auth0Domain && `https://${auth0Domain}`]
    .filter(Boolean)
    .map((url) => {
      try {
        return new URL(url).origin
      } catch {
        return null
      }
    })
    .filter(Boolean)
}

export const authRedirectUri = () => runtime("REACT_APP_AUTH0_CALLBACK_URL") || `${window.location.origin}${process.env.PUBLIC_URL ?? "/"}`

/**
 * Capability flags (plan §7.4).
 *
 * Not every provider can do everything. Rendering a "Configure SSO" button that opens
 * nothing, or a passwordless button that cannot work, is worse than hiding it — so
 * the UI asks here instead of assuming Auth0.
 */
export const authCapabilities = () => {
  const keycloak = isKeycloak()
  return {
    // Auth0 Self-Service SSO Profiles have no Keycloak equivalent. On Keycloak,
    // SSO is configured by an operator until the in-app wizard is built.
    selfServiceSso: !keycloak,
    // Auth0 passwordless `email` connection. Keycloak has no email-OTP
    // authenticator, so players use the server-minted OTP plane instead.
    idpPasswordless: !keycloak,
    // The server-minted 6-digit email OTP plane works on BOTH providers.
    serverEmailOtp: true,
    // Auth0 Organizations `organization` param on /authorize.
    orgScopedLogin: !keycloak,
  }
}
